import React, { useCallback, useRef } from 'react';

import { useIntl } from 'react-intl';

import Button from '@mui/material/Button';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';

import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { Styled } from 'src/pages/ProjectDetailsPage/components/DailyBriefings/index.styled';

import messages from './messages';

interface ArchiveButtonProps {
  archiving: boolean;
  onArchive: (file: File, onSuccess: () => void) => void;
}

export function ArchiveButton({ archiving, onArchive }: ArchiveButtonProps) {
  const { formatMessage } = useIntl();
  const importField = useRef<HTMLInputElement | null>(null);

  const handleFileSelect = useCallback(
    (handleOpen: (event: React.ChangeEvent<HTMLInputElement>) => void) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
          handleOpen(event);
        }
      },
    [],
  );

  const onClearFileField = useCallback(() => {
    if (importField.current) {
      importField.current.value = '';
    }
  }, []);

  const handleImport = useCallback(() => {
    if (importField.current?.files && importField.current.files.length > 0) {
      const file = importField.current.files[0];
      onArchive(file, onClearFileField);
    }
  }, [onArchive]);

  return (
    <ConfirmationDialog
      onConfirm={handleImport}
      message={formatMessage(messages.archiveConfirmMessage)}
    >
      {(handleOpen) => (
        <Button
          variant="outlined"
          disabled={archiving}
          startIcon={<ArchiveOutlinedIcon color="primary" />}
          component="label"
        >
          {formatMessage(messages.archiveButton)}
          <Styled.VisuallyHiddenInput
            type="file"
            ref={importField}
            onChange={handleFileSelect(handleOpen)}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            multiple
          />
        </Button>
      )}
    </ConfirmationDialog>
  );
}
